import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSidebar } from "../context/SidebarOpenContext";
import ReusableDataTable from "../hooks/ReusableDataTable";
import { Button, Modal } from "flowbite-react";
import { FaPen } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import PurchaseBillEntryForm from "../component/PurchaseBillEntryForm";
import { HiOutlineExclamationCircle } from "react-icons/hi";

function PurchaseBillsPage({ isTabbed = false }) {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isSidebarOpen } = useSidebar();
    
    const [currentView, setCurrentView] = useState("list"); 
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [deleteData, setDeleteData] = useState(null);

    const handleEditClick = (row) => {
        setSelectedPurchase(row);     
        setCurrentView("edit");        
    };

    const handleDeleteClick = (row) => {
        setDeleteData(row);
        setShowDeleteModal(true);
    };

    const fetchPurchaseBills = async () => {
        try {
            setLoading(true);
            const response = await axios.get("/purchase_bill/purchases");
            const sortedData = (response.data || []).sort((a, b) => b.id - a.id);
            setPurchases(sortedData);
        } catch (err) {
            console.error("Failed to fetch:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseBills();
    }, []);

    const confirmDelete = async () => {
        try {
            await axios.delete(`/purchase_bill/purchases/${deleteData.id}`);
            setShowDeleteModal(false);
            fetchPurchaseBills(); 
        } catch (err) {
            alert("Failed to delete");
        }
    };

    const columns = [
        { name: "S.No", selector: (row, index) => index + 1, sortable: true },
        { name: "Purchase No", selector: (row) => row.purchase_no, sortable: true },
        { name: "Date", selector: (row) => new Date(row.date).toLocaleDateString(), sortable: true },
        { name: "Vendor", selector: (row) => row.vendor_name, sortable: true },
        { name: "Material", selector: (row) => row.raw_material_name || row.fabric_content || "-", sortable: true },
        { name: "Size", selector: (row) => row.size ? `${row.size}` : "-", sortable: true },
        { name: "Quantity", selector: (row) => row.quantity, sortable: true },
        { name: "Rate", selector: (row) => parseFloat(row.rate || 0).toFixed(2), sortable: true },
        { name: "Amount", selector: (row) => parseFloat(row.amount || 0).toFixed(2), sortable: true },
        {
            name: "Option",
            cell: (row) => (
                <div className="flex gap-2">
                    <FaPen className="text-yellow-300 text-xl cursor-pointer" onClick={() => handleEditClick(row)} />
                    <MdDeleteForever className="text-red-500 text-2xl cursor-pointer" onClick={() => handleDeleteClick(row)} />
                </div>
            )
        }
    ];

    const containerClass = isTabbed 
        ? "p-0 bg-transparent" 
        : `${isSidebarOpen ? "p-4 sm:ml-64" : "p-4"} bg-[var(--content-bg)] min-h-screen mt-14`;

    return (
        <div className={containerClass}>
            
            {currentView === "list" && (
                <div className={isTabbed ? "" : "p-4 rounded-lg shadow bg-white"}>
                    <div className="flex justify-between items-center mb-4">
                        <Button 
                            onClick={() => { setSelectedPurchase(null); setCurrentView("add"); }} 
                            className="bg-[var(--icon-color)] text-[var(--sidebar-color)] font-bold hover:brightness-95 transition-all"
                        >
                            Entry Purchase Bill
                        </Button>
                    </div>
                    <ReusableDataTable columns={columns} data={purchases} />
                </div>
            )}

            {currentView === "add" && (
                <PurchaseBillEntryForm 
                    onClose={() => setCurrentView("list")} 
                    onSuccess={() => { fetchPurchaseBills(); setCurrentView("list"); }} 
                />
            )}

            {currentView === "edit" && selectedPurchase && (
                <PurchaseBillEntryForm
                    editData={selectedPurchase}
                    onClose={() => setCurrentView("list")}
                    onSuccess={() => { fetchPurchaseBills(); setCurrentView("list"); }} 
                />
            )}

            {showDeleteModal && deleteData && (
                <Modal show={showDeleteModal} size="md" onClose={() => setShowDeleteModal(false)} popup>
                    <Modal.Header />
                    <Modal.Body>
                        <div className="text-center">
                            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
                            <h3 className="mb-5 text-lg font-normal text-gray-500">Delete purchase {deleteData.purchase_no}?</h3>
                            <div className="flex justify-center gap-4">
                                <Button color="failure" onClick={confirmDelete}>Delete</Button>
                                <Button color="gray" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
}

export default PurchaseBillsPage;